import { AvailabilitiesResponse } from '../../types'

/**
 * Merges multiple AvailabilitiesResponse objects into a single response
 * This is useful for combining availabilities from different sources (e.g., regular + asego)
 *
 * The merge strategy:
 * - Combines dayAvailability from all sources
 * - Preserves existing time blocks (doesn't overwrite)
 * - Merges resourceHasCalendarIntegration flags
 * - Takes the first responseStatus from a non-undefined source
 */
const mergeAvailabilityResponses = (...responses: Array<AvailabilitiesResponse | undefined>): AvailabilitiesResponse | undefined => {
  // Filter out undefined responses
  const validResponses = responses.filter((r): r is AvailabilitiesResponse => r !== undefined)

  if (validResponses.length === 0) {
    return undefined
  }

  if (validResponses.length === 1) {
    return validResponses[0]
  }

  // Start with the first response as the base
  const [first, ...rest] = validResponses
  const merged: AvailabilitiesResponse = {
    dayAvailability: { ...first.dayAvailability },
    responseStatus: first.responseStatus,
    resourceHasCalendarIntegration: { ...first.resourceHasCalendarIntegration },
    hasAppointmentTypeMappingError: first.hasAppointmentTypeMappingError,
    firstAvailability: first.firstAvailability,
  }

  // Merge the rest
  rest.forEach((response) => {
    // Merge dayAvailability
    Object.keys(response.dayAvailability).forEach((dayKey) => {
      if (!merged.dayAvailability[dayKey]) {
        merged.dayAvailability[dayKey] = {}
      }

      Object.keys(response.dayAvailability[dayKey]).forEach((resourceId) => {
        // Only add if the resource doesn't already exist for this day
        // This preserves existing data and prioritizes the first source
        if (!merged.dayAvailability[dayKey][resourceId]) {
          merged.dayAvailability[dayKey][resourceId] = response.dayAvailability[dayKey][resourceId]
        }
      })
    })

    // Merge resourceHasCalendarIntegration
    merged.resourceHasCalendarIntegration = {
      ...merged.resourceHasCalendarIntegration,
      ...response.resourceHasCalendarIntegration,
    }

    // If we don't have a firstAvailability yet, use this one
    if (!merged.firstAvailability && response.firstAvailability) {
      merged.firstAvailability = response.firstAvailability
    }

    // If any response has appointment type mapping error, flag it
    if (response.hasAppointmentTypeMappingError) {
      merged.hasAppointmentTypeMappingError = true
    }
  })

  return merged
}

export { mergeAvailabilityResponses }
